import type { QuestionLearner, QuestionStat } from '~/gql/questions/questions'
import { useQuery } from '@apollo/client/react'
import { TrainerCapability } from '@zeus/index'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import Spinner from '~/components/Spinner'
import { GQL_MODULE_QUESTION_STATS } from '~/gql/questions/questions'
import { tourAnchor } from '~/helpers/tour/anchors'
import { useCan } from '~/hooks/use-can'
import LearnerAnswersPanel from './LearnerAnswersPanel'
import { isPeriodReversed, questionsInput } from './question-filters'
import QuestionFiltersBar from './QuestionFiltersBar'
import QuestionLearners from './QuestionLearners'
import QuestionsExports from './QuestionsExports'
import QuestionsTable from './QuestionsTable'
import { useQuestionFilters } from './use-question-filters'

/**
 * Analyse des réponses aux questions d'un module (#159). Les zones de l'écran
 * sont rendues même vides : la visite guidée doit pouvoir les montrer avant
 * qu'un module soit choisi.
 */
function QuestionsPage() {
  const { t } = useTranslation()
  const options = useQuestionFilters()
  const { filters } = options
  const canExport = useCan(TrainerCapability.STATISTICS_EXPORT)
  const [openLearner, setOpenLearner] = useState<QuestionLearner | null>(null)
  const reversed = isPeriodReversed(filters)
  const { data, loading, error } = useQuery(GQL_MODULE_QUESTION_STATS, {
    variables: { input: questionsInput(filters) },
    skip: !filters.moduleId || reversed,
  })
  const questions = (data?.moduleQuestionStats.questions ?? []) as QuestionStat[]
  const learners = (data?.moduleQuestionStats.learners ?? []) as QuestionLearner[]
  const moduleTitle = options.modules.find(module => module.id === filters.moduleId)?.title ?? ''
  // Pour la pagination (#198) : un nouveau filtre ramène les deux listes en page 1.
  const filtersKey = JSON.stringify(questionsInput(filters))
  const pathName = options.sessions.find(session => session.id === filters.sessionId)?.name ?? t('common.allSessions')

  const content = () => {
    if (!filters.moduleId) return <p className="p-6 text-center text-sm text-muted-foreground">{t('questions.pickModule')}</p>
    if (reversed) return <p role="alert" className="p-6 text-center text-sm text-destructive">{t('questions.periodReversed')}</p>
    if (loading) return <div className="flex justify-center p-10"><Spinner className="size-6" /></div>
    if (error) return <p role="alert" className="p-6 text-center text-sm text-destructive">{t('questions.loadError')}</p>
    if (questions.length === 0) return <p className="p-6 text-center text-sm text-muted-foreground">{t('questions.empty')}</p>
    return <QuestionsTable questions={questions} filtersKey={filtersKey} />
  }

  return (
    <div className="flex w-full flex-col gap-4">
      <p className="max-w-3xl text-sm text-muted-foreground">{t('questions.intro')}</p>
      <QuestionFiltersBar {...options} />
      {canExport && (
        <QuestionsExports filters={filters} disabled={questions.length === 0} pathName={pathName} moduleTitle={moduleTitle} />
      )}
      <div {...tourAnchor('questions.table')}>{content()}</div>
      <QuestionLearners learners={learners} filtersKey={filtersKey} onOpen={setOpenLearner} />
      <LearnerAnswersPanel learner={openLearner} moduleId={filters.moduleId} sessionId={filters.sessionId} onClose={() => setOpenLearner(null)} />
    </div>
  )
}

export default QuestionsPage
